import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environment';
import { NavController } from '@ionic/angular';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ToastService } from '../shared/services/toast.service';

@Injectable()
export class AuthResponseInterceptor implements HttpInterceptor {
  private authRoute = '/auth/';
  private authLoginRoute = '/auth/login';
  private authLogoutRoute = '/auth/logout';

  constructor(
    private readonly navCtrl: NavController,
    private readonly injector: Injector,
    private readonly router: Router,
  ) {
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return this._handle(req, next);
  }

  private _handle(req: HttpRequest<any>, next: HttpHandler):Observable<HttpEvent<any>>  {
    return next
      .handle(req)
      .pipe(
        tap((response: any) => {
          // GraphQL specific logic
          if (this._isGraphQLResponse(response)) {

            if (this._isGraphQLUnauthorized(response.body)) {
              throw new UnauthorizedException();
            }

            if (this._isGraphQLForbidden(response.body)) {
              throw new ForbiddenException();
            }
            
            if (response.body.errors) {
              throw new GenericGraphqlException(response.body)
            }
          }
        }),
        catchError((error) => this._handleError(error)),
      );
  }

  private _handleError(error: HttpErrorResponse): Observable<never> {
    switch (error.status) {
      case 401:
        console.log("Caught 401 error", error);
        if (!this.router.url.startsWith(this.authRoute)) {
          this.navCtrl.navigateRoot(this.authLogoutRoute);
          this._presentToast(["UNAUTHORIZED"], true);
        }
        break;

      case 403:
        console.log("Caught 403 error", error);
        if (!this.router.url.startsWith(this.authRoute)) {
          this._presentToast(["FORBIDDEN"], true);
        }
        break;
    
      default:
        if (error instanceof GenericGraphqlException) {
          console.log("Caught GenericGraphqlException", error);
          this._presentToast(error.errors.map((e) => e.extensions.code), true);
        } else {
          console.log("Caught Other error", error);
          this._presentToast(["GENERAL"], true);
        }
        break;
    }

    return throwError(error);
  }

  private _isGraphQLResponse(response: any): boolean {
    const graphQLUrl = environment['graphqlUrl'];
    return (
      !!response &&
      response.status === 200 &&
      !!response.url &&
      response.url.startsWith(graphQLUrl) &&
      !!response.body
    );
  }

  //TODO Conform to a better standard with http codes under Extensions
  private _isGraphQLUnauthorized(body: any): boolean {
    const data = body.data;
    const errors: Array<any> = body.errors;
    return !data && errors.some((e) => e.message === 'Unauthorized');
  }

  //TODO Conform to a better standard with http codes under Extensions
  private _isGraphQLForbidden(body: any): boolean {
    const data = body.data;
    const errors: Array<any> = body.errors;
    return !data && errors.some((e) => e.message === 'Forbidden');
  }

  /**
   * @param translationKeys array of translation keys
   */
  private async _presentToast(translationKeys: string[], error: boolean): Promise<void> {
    const toastService = this.injector.get<ToastService>(ToastService);
    if (error)
      await toastService.presentError(translationKeys);
    else
      await toastService.presentMessage(translationKeys);
  }
}

class UnauthorizedException extends HttpErrorResponse {
  constructor() {
    super({status: 401, statusText: "Unauthorized"});
  }
}

class ForbiddenException extends HttpErrorResponse {
  constructor() {
    super({status: 403, statusText: "Forbidden"});
  }
}

class GenericGraphqlException extends HttpErrorResponse {
  errors:{extensions: {code: string}, message: string}[];
  constructor(body: any) {
    super({status: 500, statusText: "General error"});
    this.errors = body.errors;
  }
}
